import api from './api';

/* Notification routing matrix — who gets told about what.
 *
 * event × recipient relation → channels (+ an optional "only from the Nth
 * occurrence" threshold). The catalog is served by the backend rather than
 * hardcoded here, so a newly supported event shows up in the grid without a
 * frontend release.
 */

export type RoutingChannel = 'bell' | 'push' | 'email';

export interface RoutingEvent {
  key: string;
  label: string;
  description: string;
  supports_threshold: boolean;
  threshold_label?: string;
}

export interface RoutingRecipient {
  key: string;
  label: string;
}

export interface RoutingCatalog {
  events: RoutingEvent[];
  recipients: RoutingRecipient[];
  channels: RoutingChannel[];
}

export interface RoutingCell {
  bell: boolean;
  push: boolean;
  email: boolean;
  threshold: number | null;
}

/** { [event]: { [recipient]: cell } } — always a full grid, defaults filled in. */
export type RoutingMatrix = Record<string, Record<string, RoutingCell>>;

const detail = (e: any, fallback: string) =>
  e?.response?.data?.detail || e?.message || fallback;

export const getRoutingCatalog = async (): Promise<RoutingCatalog> => {
  const res = await api.get('/notification-routing/catalog');
  return res.data;
};

export const getNotificationRouting = async (): Promise<RoutingMatrix> => {
  const res = await api.get('/notification-routing');
  return res.data.routing ?? {};
};

export const saveNotificationRouting = async (
  matrix: RoutingMatrix
): Promise<RoutingMatrix> => {
  // The backend replaces every event present in the payload, so send the grid
  // whole — a relation switched off has to disappear, not linger as a row.
  const cells = Object.entries(matrix).flatMap(([event, recipients]) =>
    Object.entries(recipients).map(([recipient, cell]) => ({
      event,
      recipient,
      bell: !!cell.bell,
      push: !!cell.push,
      email: !!cell.email,
      threshold: cell.threshold ?? null,
    }))
  );
  try {
    const res = await api.put('/notification-routing', { cells });
    return res.data.routing ?? {};
  } catch (e: any) {
    throw new Error(detail(e, 'Could not save notification routing'));
  }
};
